import { BulkUserRowSchema, InviteRowSchema } from './validationSchemes';

export interface CsvParseResult {
    headers: string[];
    rows: string[][];
    errors: string[];
}

export function parseCsv(text: string): CsvParseResult {
    const errors: string[] = [];
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

    if (lines.length === 0) {
        return { headers: [], rows: [], errors: ['File is empty'] };
    }

    const headers = parseCsvLine(lines[0]);
    const rows: string[][] = [];

    for (let i = 1; i < lines.length; i++) {
        const row = parseCsvLine(lines[i]);
        if (row.length !== headers.length) {
            errors.push(`Row ${i} has ${row.length} columns, expected ${headers.length}`);
        }
        rows.push(row);
    }

    return { headers, rows, errors };
}

function parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotes) {
            if (char === '"') {
                if (i + 1 < line.length && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                current += char;
            }
        } else {
            if (char === '"') {
                inQuotes = true;
            } else if (char === ',') {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
    }
    result.push(current.trim());
    return result;
}

export interface BulkUserRow {
    userName: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
}

export interface FieldMapping {
    systemField: keyof BulkUserRow;
    label: string;
    csvColumn: string;
    required: boolean;
}

export const BULK_USER_FIELDS: FieldMapping[] = [
    { systemField: 'userName', label: 'Username', csvColumn: '', required: true },
    { systemField: 'email', label: 'Email', csvColumn: '', required: true },
    { systemField: 'firstName', label: 'First Name', csvColumn: '', required: true },
    { systemField: 'lastName', label: 'Last Name', csvColumn: '', required: true },
    { systemField: 'phoneNumber', label: 'Phone Number', csvColumn: '', required: false },
];

// One column: the username is the email and the Auth0 form collects the rest at acceptance.
export const INVITE_USER_FIELDS: FieldMapping[] = [
    { systemField: 'email', label: 'Email', csvColumn: '', required: true },
];

export function autoMapFields(headers: string[], fields: FieldMapping[]): FieldMapping[] {
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const aliases: Record<string, string[]> = {
        userName: ['username', 'user', 'login', 'userid'],
        email: ['email', 'emailaddress', 'mail'],
        firstName: ['firstname', 'first', 'givenname'],
        lastName: ['lastname', 'last', 'surname', 'familyname'],
        phoneNumber: ['phonenumber', 'phone', 'telephone', 'mobile', 'tel'],
    };

    return fields.map(field => {
        const fieldAliases = aliases[field.systemField] || [normalize(field.systemField)];
        const match = headers.find(h => fieldAliases.includes(normalize(h)));
        return { ...field, csvColumn: match || '' };
    });
}

export function mapRowsToUsers(rows: string[][], headers: string[], mappings: FieldMapping[]): BulkUserRow[] {
    return rows.map(row => {
        // Every field present, so a mode that maps fewer columns never produces `undefined` where the
        // interface promises a string.
        const user: BulkUserRow = { userName: '', email: '', firstName: '', lastName: '', phoneNumber: '' };
        for (const mapping of mappings) {
            const colIndex = headers.indexOf(mapping.csvColumn);
            user[mapping.systemField] = colIndex >= 0 ? (row[colIndex] || '') : '';
        }
        return user;
    });
}

export interface RowValidationError {
    rowIndex: number;
    field: string;
    message: string;
}

export function validateBulkUsers(users: BulkUserRow[]): RowValidationError[] {
    const errors: RowValidationError[] = [];
    const seenUserNames = new Map<string, number>();
    const seenEmails = new Map<string, number>();

    users.forEach((user, i) => {
        // Validate each row using the shared Yup schema
        try {
            BulkUserRowSchema.validateSync(user, { abortEarly: false });
        } catch (err: any) {
            if (err.inner) {
                for (const validationError of err.inner) {
                    errors.push({
                        rowIndex: i,
                        field: validationError.path || '',
                        message: validationError.message,
                    });
                }
            }
        }

        // Check for duplicate username within the CSV
        if (user.userName.trim()) {
            const lower = user.userName.trim().toLowerCase();
            if (seenUserNames.has(lower)) {
                errors.push({ rowIndex: i, field: 'userName', message: `Duplicate username (same as row ${seenUserNames.get(lower)! + 1})` });
            }
            seenUserNames.set(lower, i);
        }

        // Check for duplicate email within the CSV
        if (user.email.trim()) {
            const lower = user.email.trim().toLowerCase();
            if (seenEmails.has(lower)) {
                errors.push({ rowIndex: i, field: 'email', message: `Duplicate email (same as row ${seenEmails.get(lower)! + 1})` });
            }
            seenEmails.set(lower, i);
        }
    });

    return errors;
}

export function validateInviteRows(users: BulkUserRow[]): RowValidationError[] {
    const errors: RowValidationError[] = [];
    const seenEmails = new Map<string, number>();

    users.forEach((user, i) => {
        try {
            InviteRowSchema.validateSync(user, { abortEarly: false });
        } catch (err: any) {
            for (const validationError of err.inner ?? []) {
                errors.push({ rowIndex: i, field: validationError.path || '', message: validationError.message });
            }
        }

        const lower = user.email.trim().toLowerCase();
        if (lower) {
            if (seenEmails.has(lower)) {
                errors.push({ rowIndex: i, field: 'email', message: `Duplicate email (same as row ${seenEmails.get(lower)! + 1})` });
            }
            seenEmails.set(lower, i);
        }
    });

    return errors;
}
